// Shared post-bond market figures for 01.
//
// Pre-bond the bonding curve WAS the only market, so its spotPrice() was the
// price. Once 01 bonds, issuance closes and the real trading price is set on
// secondary markets (Uniswap). The curve's spotPrice() then means only the
// buyback FLOOR the reserve will always pay.
//
// This hook is the single source of truth for the market price and market cap
// so the stat strip, the Info panel, and the dapp all agree. The canonical
// market price comes from the api-server (deepest qualifying pool, junk pools
// filtered out); circulating supply is the on-chain coin.totalSupply(). Market
// cap is deliberately marketPrice x circulating, never the market-data FDV
// (whose implied supply does not match the chain).
import { formatUnits } from "viem";
import { usePools } from "@/lib/pools";
import { useCurveStats } from "@/lib/curve-stats";

export interface MarketStats {
  /** Canonical USD market price for 01, or null when no market exists yet. */
  marketPriceUsd: number | null;
  /** Name of the pool the market price was taken from, or null. */
  marketPoolName: string | null;
  /** Circulating 01 (coin.totalSupply()), or undefined while loading. */
  circulating?: number;
  /** marketPriceUsd x circulating, or undefined when either is missing. */
  marketCapUsd?: number;
  isLoading: boolean;
}

export function useMarketStats(): MarketStats {
  const pools = usePools();
  const stats = useCurveStats();
  const marketPriceUsd = pools.data?.marketPriceUsd ?? null;
  const marketPoolName = pools.data?.marketPoolName ?? null;
  const circulating =
    stats.totalSupply !== undefined
      ? Number(formatUnits(stats.totalSupply, 18))
      : undefined;
  const marketCapUsd =
    marketPriceUsd !== null && circulating !== undefined
      ? marketPriceUsd * circulating
      : undefined;
  return {
    marketPriceUsd,
    marketPoolName,
    circulating,
    marketCapUsd,
    isLoading: pools.isLoading,
  };
}
